import React, { useEffect } from 'react';
import { findProductById } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { ProductProvider, useProductContext } from '@jbhi-fi/components/src/context/ProductProvider';
import { ProductFallback } from '@jbhi-fi/components/src/ProductFallback/ProductFallback';
import { ArticleRichTextLayoutBlockContextProvider } from '../../hooks/useArticleRichTextLayoutBlockProps';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getStoreCurrency } from '../../../../../utils/shopify/getStoreCurrency';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { submitTracking } from '@jbhi-fi/components/src/Tracking/utils/shopifyActionTrackingHelper';
import { styles } from './ArticleProduct.css';

export type ArticleProductProps = { heading: string; content: React.ReactNode | string; productVariantId: string };

export const ArticleProduct = ({ heading, content, productVariantId }: ArticleProductProps) => {
  const { products, isLoading } = useProductContext();
  const product = findProductById(products, productVariantId);
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const productGtm = product?.google_tag_manager;

  useEffect(() => {
    if (product) {
      trackViewItemList({
        currency: getStoreCurrency(),
        listName: 'stack',
        viewedItems: transformAlgoliaHitForTracking(product),
      });
    }

    submitTracking({
      event: 'productImpression',
      trackingDescriptor: {
        page: 'STACK',
        currency: getStoreCurrency(),
        ecommerceExtraProps: {
          ...productGtm,
          list: `stack`,
        },
      },
      gtm: {},
    });
  }, [product]);

  const trackingDescriptor = {
    page: 'STACK',
    group: 'productArticle',
    currency: getStoreCurrency(),
    ecommerceExtraProps: {
      list: 'STACK - Product Article',
    },
  };

  return (
    <div className={styles.container}>
      <div className={styles.textContainer}>
        <h3 className={styles.heading}>{heading}</h3>
        <ArticleRichTextLayoutBlockContextProvider paddingRight={0} paddingLeft={0} paddingTop={2} paddingBottom={2}>
          <div className={styles.content}>{content}</div>
        </ArticleRichTextLayoutBlockContextProvider>
      </div>
      <div className={styles.product}>
        <ProductFallback isLoading={isLoading} product={product}>
          {product ? (
            <ProductCard
              {...AlgoliaHitTransformer.toProductCardProps(product, {
                trackingDescriptor,
              })}
              {...productCartPropsOverrides}
              variant="tablet"
              trackingInformation={{
                currency: getStoreCurrency(),
                gtm: product.google_tag_manager,
                trackingDescriptor: {
                  page: 'STACK',
                  group: 'productArticle',
                  currency: getStoreCurrency(),
                  ecommerceExtraProps: {
                    list: 'stack',
                  },
                },
              }}
              getIsCompared={isAddedToCompare}
              onCompare={compareHandleClick}
              getShouldShowCompare={shouldShowCompare}
              isLoggedIn={isLoggedIn}
              onUpdateWishlist={handleUpdateWishlist}
              getIsInWishlist={getIsInWishlist}
              onAddToCart={
                onAddToCart
                  ? (e, _sku, variantId, eventData) => onAddToCart(e, product, variantId, eventData?.trackingDescriptor)
                  : undefined
              }
            />
          ) : null}
        </ProductFallback>
      </div>
    </div>
  );
};

export const ArticleProductWithProvider = ({ heading, content, productVariantId }: ArticleProductProps) => {
  return (
    <ProductProvider productVariantIds={[productVariantId]}>
      <ArticleProduct heading={heading} content={content} productVariantId={productVariantId} />
    </ProductProvider>
  );
};
